import { useContext } from "react";

// constants
import { AppContext } from "../../app-context";
import { CUSTOM_EVENTS } from "../../constants/misc";
import { createCustomEvents } from "../../methods/createCustomEvents";

// services
import { handleUpdateCompanyDetails, useUpdateCompanyDetails } from "../../_services/hooks/useUserProfileService";
import { AuthServiceAdminConsole } from "../../_services/auth.adminConsole.service";
import { CompanyServiceType } from "../../constants/serviceTypeId";
import { AuthService } from "../../_services/auth.service";
// ga4
// ga4
import { ga4SetUser, sendPageSenseEvent, getFBEventId } from "../../_services/user-analytics.service";

// components
import { toast } from "components/toast/toast";

// hooks
import { LoginScope, useSendOTP } from "../../_services/sms.service";
import { useSendWhatsappOTP } from "../../_services/whatsapp-otp.service";

export interface OnClickResendOTPParams {
    onSuccessCallback: () => void;
    onSettledCallback?: () => void;
    isWhatsApp?: boolean;
}

export function useAuth({
    phoneNum,
    otp,
    name,
    serviceType,
    whatsappUpdatesEnabled,
    placeId,
    isInitiatedFrom,
    referralCode,
    otpScope,
    user_joining_bonus_enabled
}: {
    phoneNum?: string | number;
    otp?: number;
    name?: string;
    serviceType?: string;
    whatsappUpdatesEnabled?: boolean;
    placeId?: string;
    isInitiatedFrom?: string;
    referralCode?: string;
    otpScope: LoginScope;
    user_joining_bonus_enabled?: boolean;
}) {
    const {
        setPhoneNum,
        setName,
        setCompanyId,
        isPhoneVerified,
        setIsPhoneVerified,
        setCompanyLocation,
        setCompanyLogo,
        setUserId,
        userId,
        setServiceType,
        lang,
        setLang,
        setCsDashboardUserRoles,
        isCsDashboardUser,
        sessionId,
        setSessionId
    } = useContext(AppContext);

    const { mutateAsync: updateCompanyDetails } = useUpdateCompanyDetails();
    const { mutate: sendOTP } = useSendOTP();
    const { mutate: sendWaOTP } = useSendWhatsappOTP();

    const onSubmitPhoneNum = async (storePhoneNum: boolean = true) => {
        if (!phoneNum) {
            // isPhoneVerified removed
            return;
        }

        !!storePhoneNum && setPhoneNum?.(phoneNum);
        sendOTP(
            { phoneNumber: phoneNum, scope: otpScope },
            {
                onSuccess: (res) => {
                    if (res && res.data && res.data.message) {
                        toast.success(res.data.message);
                    } else {
                        toast.success("OTP sent successfully!");
                    }
                },
                onError: (res) => {
                    if (res && res.message) {
                        toast.error(res.message);
                    } else {
                        toast.error("Error! Could not send the OTP.");
                    }
                }
            }
        );
    };

    const sendOtpViaWhatsapp = async ({ onSuccessCallback, onSettledCallback }: OnClickResendOTPParams) => {
        if (!phoneNum) {
            onSettledCallback?.();
            return false;
        }

        sendWaOTP(
            { phoneNumber: phoneNum },
            {
                onSuccess: (res) => {
                    if (res && res.data && res.data.message) {
                        toast.success(res.data.message);
                    } else {
                        toast.success("OTP sent successfully!");
                    }

                    onSuccessCallback();
                    onSettledCallback?.();
                },
                onError: (res) => {
                    if (res && res.message) {
                        toast.error(res.message);
                    } else {
                        toast.error("Error! Could not send the OTP.");
                    }
                    onSettledCallback?.();
                }
            }
        );
        return false;
    };
    const onClickResendOtp = async ({
        isWhatsApp = false,
        onSuccessCallback,
        onSettledCallback
    }: OnClickResendOTPParams) => {
        if (!phoneNum) {
            onSettledCallback?.();
            return false;
        }

        sendOTP(
            { phoneNumber: phoneNum, scope: otpScope, isWhatsApp },
            {
                onSuccess: (res) => {
                    if (res && res.data && res.data.message) {
                        toast.success(res.data.message);
                    } else {
                        toast.success("OTP sent successfully!");
                    }

                    onSuccessCallback();
                    onSettledCallback?.();
                },
                onError: (res) => {
                    if (res && res.message) {
                        toast.error(res.message);
                    } else {
                        toast.error("Error! Could not send the OTP.");
                    }
                    onSettledCallback?.();
                }
            }
        );
        return false;
    };

    const addLoginOrRegisterSuccessfullEvent = () => {
        createCustomEvents({ eventName: CUSTOM_EVENTS.LOGGEDIN });
    };

    const onSubmitOtp = async (override?: { phoneNum?: string | number; otp?: number }) => {
        const resolvedPhoneNum = override?.phoneNum ?? phoneNum;
        const resolvedOtp = override?.otp ?? otp;

        if (!resolvedPhoneNum || !resolvedOtp) {
            return;
        }
        const { data } = await AuthService.loginViaOtp({
            phone_number: resolvedPhoneNum,
            otp: resolvedOtp,
            is_wa_optin: whatsappUpdatesEnabled
        });
        setSessionId?.(data?.session_id);

        // User model exists
        if (data) {
            if (isCsDashboardUser) {
                const userRoles = await AuthServiceAdminConsole.getUserRoles(data.u_id);
                if (userRoles?.id) {
                    userRoles && setCsDashboardUserRoles?.(userRoles.roles);
                } else {
                    toast.error("No access, Please reach out to manager.");
                }
            }

            // login
            setIsPhoneVerified?.(true);
            setName?.(data.c_name);
            setCompanyId?.(data.c_id);
            setCompanyLocation?.(data.c_loc);
            setUserId?.(data.u_id);
            if (lang) {
                handleUpdateCompanyDetails(updateCompanyDetails, {
                    companyId: data?.c_id,
                    languageCode: lang
                });
            } else {
                setLang?.(data.lang);
            }
            setCompanyLogo?.(data?.profile_image);
            if (data.c_serv) setServiceType?.(data.c_serv);

            if (data.c_id) {
                const eventID = getFBEventId("login", data?.c_id);
                window.fbq?.("track", "AddToCart", { phoneNum }, { eventID });
                // sendFbCapiEvent({ event_id: eventID, event_name: "AddToCart", phone_num: phoneNum });

                sendPageSenseEvent("Login Success");
            } else {
                // User Registration
                const eventID = getFBEventId("register", data?.u_id);
                window.fbq?.("track", "AddToWishlist", { phoneNum }, { eventID });
                // sendFbCapiEvent({ event_id: eventID, event_name: "AddToWishlist", phone_num: phoneNum });

                sendPageSenseEvent("User Registration Succes");
            }
            ga4SetUser(data.u_id);
            addLoginOrRegisterSuccessfullEvent();
            return {
                status: true,
                companyName: data.c_name,
                companyId: data.c_id,
                serviceType: data.c_serv,
                userId: data.u_id,
                createdAt: data.user_created_at
            };
        }
    };

    const onSubmitCompanyDetails = async (override?: {
        name?: string;
        userId?: number | string;
        serviceType?: string;
        placeId?: string;
    }) => {
        const resolvedName = override?.name ?? name;
        const resolvedUserId = override?.userId ?? userId;
        const resolvedServiceType = override?.serviceType ?? serviceType;
        const resolvedPlaceId = override?.placeId ?? placeId;

        // company sign up
        if (!resolvedName || !resolvedUserId || !resolvedServiceType) {
            return;
        }
        setName?.(resolvedName);
        const data = await AuthService.registerCompanyDetails({
            userId: parseInt(String(resolvedUserId), 10),
            name: resolvedName,
            serviceType: resolvedServiceType as CompanyServiceType,
            placeId: resolvedPlaceId || "",
            referralCode: referralCode,
            sessionId: sessionId!,
            user_joining_bonus_enabled
        });

        if (!data) {
            toast.error("Something went wrong, Please try again later or contact customer support!");
            return { status: false, companyName: null, companyId: null };
        }

        /**
         * As this is the last step,
         * here is where we complete the signup process
         */
        setCompanyId?.(data?.c_id);
        if (lang) {
            handleUpdateCompanyDetails(updateCompanyDetails, {
                companyId: data?.c_id,
                languageCode: lang
            });
        }
        setCompanyLogo?.(data?.profile_image);
        setServiceType?.(resolvedServiceType);

        const eventID = getFBEventId("signup", data?.c_id);
        window.fbq?.("track", "CompleteRegistration", { phoneNum }, { eventID });
        // sendFbCapiEvent({ event_id: eventID, event_name: "CompleteRegistration", phone_num: phoneNum });

        sendPageSenseEvent("Signup Succes");
        return {
            status: true,
            companyName: data.c_name,
            companyId: data.c_id,
            userId: data.u_id,
            createdAt: data.user_created_at
        };
    };

    return {
        onSubmitPhoneNum,
        onClickResendOtp,
        onSubmitOtp,
        onSubmitCompanyDetails,
        sendOtpViaWhatsapp
    };
}
